import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppEmployerMultiSelect from '@nxsys/components/AppEmployerMultiSelect';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import { aepnEnrolmentStatusOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { GetAEPNListParams } from '@nxsys/store/slices/aepn/types';
import { hasFilterValue } from '@nxsys/utils/filterForm';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import moment, { Moment } from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useEmployerSearchScope } from '@nxsys/hooks/useEmployerSearchScope';
import { useEmployerFieldLabels } from '@nxsys/hooks/useEmployerFieldLabels';

type FilterModalProps = {
  isOpen: boolean;
  title: string;
  toggle: () => void;
  onFilter: (params: GetAEPNListParams) => void;
  onChangeCompanyIds?: (companyIds: string[]) => void;
};

const FilterModal: React.FC<FilterModalProps> = ({
  title,
  isOpen,
  toggle,
  onFilter,
  onChangeCompanyIds,
}: FilterModalProps) => {
  const { messages } = useTypedIntl();
  const employerLabels = useEmployerFieldLabels();
  const employerSearchScope = useEmployerSearchScope();

  const { aepnList: { queryParams } } = useSelector(
    (state: AppState) => state.aepnStore,
  );

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const hasFilter = hasFilterValue(watchValue);
  // Keep Apply enabled once a filter is applied, so Clear All + Apply can remove it.
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);

  const filterOptions = [
    {
      label: 'AEPN Issue Date',
      name: 'aepnIssueDate',
      filterType: 'date',
      placeholder: messages["common.selectDate"],
    },
    {
      label: 'AEPN Download Date',
      name: 'aepnDownloadDateTime',
      filterType: 'date',
      placeholder: messages["common.selectDate"],
    },
    {
      label: 'AEPN Enrolment Date',
      name: 'aepnEnrolmentDate',
      filterType: 'date',
      placeholder: messages["common.selectDate"],
    },
    {
      label: "Enrolment Status",
      name: 'enrolmentStatus',
      filterType: 'select',
      options: aepnEnrolmentStatusOptions,
      placeholder: "Select status",
    },
  ];

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    const companyIds: string[] = values?.companyIds ?? [];
    onChangeCompanyIds?.(companyIds);
    const params = {
      ...queryParams,
      page: 0,
      aepnIssueDate: values?.aepnIssueDate ? moment(values?.aepnIssueDate).format('YYYY-MM-DD') : undefined,
      aepnDownloadDateTime: values?.aepnDownloadDateTime ? moment(values?.aepnDownloadDateTime).format('YYYY-MM-DD') : undefined,
      aepnEnrolmentDate: values?.aepnEnrolmentDate ? moment(values?.aepnEnrolmentDate).format('YYYY-MM-DD') : undefined,
      enrolmentStatus: values?.enrolmentStatus,
      companyIds: companyIds.length > 0 ? companyIds : undefined,
    };
    setHasAppliedFilter(hasFilterValue(values));
    onFilter(params);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages["common.cancel"],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages["common.apply"],
          buttonType: 'primary',
          onClick: () => form.submit(),
          disabled: !hasFilter && !hasAppliedFilter,
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages["common.clearAll"]}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!hasFilter}
          />
        </div>
      }
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Form.Item
          label={employerLabels.label}
          name="companyIds"
          layout="vertical"
        >
          <AppEmployerMultiSelect
            apiType="searchCompany"
            type="company"
            placeholder={employerLabels.placeholder}
            paramFilter={employerSearchScope}
          />
        </Form.Item>
        {filterOptions?.map((f) => {
          return (
            <Form.Item
              key={f.name}
              label={f.label}
              name={f.name}
              layout="vertical"
            >
              {f.filterType === 'select' && (
                <AppSelect
                  placeholder={f.placeholder}
                  options={f.options ?? []}
                  onChange={(value) => {
                    form.setFieldsValue({
                      [f.name]: value.value,
                    });
                  }}
                />
              )}
              {f.filterType === 'date' && (
                <AppDatePicker
                  placeholder={f.placeholder}
                  onChange={(date: Moment) => {
                    form.setFieldsValue({
                      [f.name]: date.toISOString(),
                    });
                  }}
                  format="DD/MM/YYYY"
                />
              )}
            </Form.Item>
          );
        })}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default FilterModal;
